import React from "react";
import { Field, ErrorMessage } from "formik";
import {
  FaUser, FaCalendarAlt, FaMapMarkerAlt,
  FaEnvelope, FaPhone, FaCar, FaAllergies, FaWheelchair,
} from "react-icons/fa";
import { FaNoteSticky } from "react-icons/fa6";
import FormField from "../components/FormField";
import InfoTooltip from "../components/InfoTooltip";
import RadioButton from "../components/RadioButton";
import { Checkbox } from "../components/ui/checkbox";
import { cn } from "../lib/utils";

export const Step1 = () => (
  <div className="h-full flex flex-col gap-3.5 step-enter">

    <div>
      <h2 className="text-base font-bold text-foreground">Informations personnelles</h2>
      <p className="text-xs text-muted-foreground mt-0.5">Remplissez vos informations d'identité et de contact.</p>
    </div>

    {/* Nom / Prénom */}
    <div className="grid grid-cols-2 gap-3">
      <FormField name="nom"    label="Nom"    icon={FaUser} required />
      <FormField name="prenom" label="Prénom" icon={FaUser} required />
    </div>

    {/* Pseudo / Date de naissance */}
    <div className="grid grid-cols-2 gap-3">
      <FormField
        name="pseudo"
        label="Pseudo ImagiGeeks"
        icon={FaUser}
        required
        hasInfo
        infoTitle="Pseudo ImagiGeeks"
        info="Ce pseudo sera affiché sur ta carte de membre ImagiGeeks et utilisé sur nos réseaux sociaux et communications officielles de l'association."
      />
      <FormField name="dateNaissance"  label="Date de naissance"  icon={FaCalendarAlt} type="date" required />
    </div>

    {/* Adresse / CP / Ville */}
    <div className="grid grid-cols-[2fr_1fr_1fr] gap-3">
      <FormField name="adressePostale" label="Adresse postale" icon={FaMapMarkerAlt} required />
      <FormField name="zipCode"        label="Code postal"     icon={FaMapMarkerAlt} required />
      <FormField name="city"           label="Ville"           icon={FaMapMarkerAlt} required />
    </div>

    {/* Email / Téléphone */}
    <div className="grid grid-cols-2 gap-3">
      <FormField name="email"     label="Adresse mail" icon={FaEnvelope} type="email" required />
      <FormField name="telephone" label="Téléphone"    icon={FaPhone}    type="tel"   required />
    </div>

    {/* Véhiculé + PMR */}
    <div className="grid grid-cols-2 gap-3 items-start">

      {/* Véhiculé */}
      <div className="flex flex-col gap-1">
        <label className="flex items-center gap-1.5 text-xs font-semibold text-foreground/75 uppercase tracking-wide select-none">
          <FaCar className="h-3 w-3 text-primary" />
          Véhiculé.e <span className="text-secondary font-bold">*</span>
        </label>
        <div className="flex gap-1.5 flex-wrap">
          {["Oui", "Non"].map((o) => (
            <RadioButton key={o} name="vehicule" value={o} label={o} />
          ))}
        </div>
        <ErrorMessage name="vehicule" component="p" className="text-[10px] text-destructive" />
      </div>

      {/* PMR */}
      <div className="flex flex-col gap-1">
        <span className="text-xs font-semibold text-foreground/75 uppercase tracking-wide select-none invisible">
          PMR
        </span>
        <div className="flex items-center justify-between h-9 px-3 rounded-lg border border-border/60 bg-background/50 hover:border-border transition-colors">
          <Field name="pmr">
            {({ field, form }) => (
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <Checkbox
                  id="pmr"
                  checked={field.value === true}
                  onCheckedChange={(v) => form.setFieldValue("pmr", v)}
                />
                <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">
                  <FaWheelchair className="h-3 w-3 text-primary" />
                  PMR
                </span>
              </label>
            )}
          </Field>
          <InfoTooltip
            title="Mention PMR"
            text="La mention PMR est recueillie afin d'anticiper et d'organiser, si nécessaire, des solutions d'accès adaptées pour les personnes à mobilité réduite, notamment lors de l'hébergement ou d'autres situations spécifiques."
          />
        </div>
      </div>
    </div>

    {/* Allergies + Notes — en dessous, prennent toute la largeur */}
    <div className="grid grid-cols-2 gap-3 flex-1 min-h-0">
      <FormField
        name="allergies" label="Allergies" icon={FaAllergies}
        as="textarea" rows={3}
        hasInfo infoTitle="Informations Importantes"
        info="Cette information est recueillie pour faciliter l'organisation des repas lors d'événements, qu'ils soient fournis par la convention ou partagés avec l'association. Elle peut également être utilisée en cas d'urgence médicale."
      />
      <FormField
        name="notes" label="Notes personnelles" icon={FaNoteSticky}
        as="textarea" rows={3}
        hasInfo infoTitle="Notes Importantes"
        info="Les notes personnelles sont des informations supplémentaires que vous souhaitez nous communiquer. Elles peuvent inclure des détails importants ou spécifiques."
      />
    </div>
  </div>
);
